import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Play, Search, Filter, Music, Clock, TrendingUp } from "lucide-react";
import axios from "axios";
import { useAudio } from "../App";
import { useTranslation } from "react-i18next";

const BACKEND_URL = "";
const API = `${BACKEND_URL}/api`;

const CatalogPage = () => {
  const { t } = useTranslation();
  const [tracks, setTracks] = useState([]);
  const [filteredTracks, setFilteredTracks] = useState([]);
  const [genres, setGenres] = useState([]);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGenre, setSelectedGenre] = useState("");
  const [selectedArtist, setSelectedArtist] = useState("");
  const { playTrack, currentTrack, isPlaying } = useAudio();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tracksRes, genresRes, artistsRes] = await Promise.all([
          axios.get(`${API}/tracks`),
          axios.get(`${API}/genres`),
          axios.get(`${API}/artists`)
        ]);
        setTracks(tracksRes.data.tracks || tracksRes.data);
        setFilteredTracks(tracksRes.data.tracks || tracksRes.data);
        setGenres(genresRes.data.genres || []);
        setArtists(artistsRes.data.artists || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    let result = tracks;

    if (searchQuery) {
      result = result.filter(track =>
        track.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        track.artist.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (selectedGenre) {
      result = result.filter(track => track.genre === selectedGenre);
    }

    if (selectedArtist) {
      result = result.filter(track => track.artist === selectedArtist);
    }

    setFilteredTracks(result);
  }, [searchQuery, selectedGenre, selectedArtist, tracks]);

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedGenre("");
    setSelectedArtist("");
  };

  const uniqueArtists = [...new Set(tracks.map(t => t.artist))];

  return (
    <div className="min-h-screen py-12 px-6 md:px-12" data-testid="catalog-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <span className="font-heading text-sm tracking-widest text-[#FFB347] mb-2 block">
            CATALOGUE
          </span>
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-4">
            {t('catalog.title')}
          </h1>
          <p className="text-[#A8A29E] text-lg max-w-2xl">
            {t('catalog.subtitle')}
          </p>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8 p-6 bg-[#141210] rounded-xl border border-[rgba(139,69,19,0.2)]"
          data-testid="filters-section"
        >
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#A8A29E] pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('catalog.search')}
                className="w-full bg-[#0A0908] border border-[rgba(139,69,19,0.3)] rounded-lg px-4 py-3 pl-14 text-[#F5F5F0] focus:border-[#FFB347] focus:outline-none"
                data-testid="search-input"
              />
            </div>

            {/* Genre Filter */}
            <div className="relative min-w-[200px]">
              <Filter className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#A8A29E] pointer-events-none" />
              <select
                value={selectedGenre}
                onChange={(e) => setSelectedGenre(e.target.value)}
                className="w-full bg-[#0A0908] border border-[rgba(139,69,19,0.3)] rounded-lg px-4 py-3 pl-14 text-[#F5F5F0] focus:border-[#FFB347] focus:outline-none appearance-none cursor-pointer"
                data-testid="genre-filter"
              >
                <option value="">{t('catalog.allGenres')}</option>
                {genres.map(genre => (
                  <option key={genre} value={genre}>{genre}</option>
                ))}
              </select>
            </div>

            {/* Artist Filter */}
            <div className="relative min-w-[200px]">
              <Music className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#A8A29E] pointer-events-none" />
              <select
                value={selectedArtist}
                onChange={(e) => setSelectedArtist(e.target.value)}
                className="w-full bg-[#0A0908] border border-[rgba(139,69,19,0.3)] rounded-lg px-4 py-3 pl-14 text-[#F5F5F0] focus:border-[#FFB347] focus:outline-none appearance-none cursor-pointer"
                data-testid="artist-filter"
              >
                <option value="">{t('artists.title')}</option>
                {uniqueArtists.map(artist => (
                  <option key={artist} value={artist}>{artist}</option>
                ))}
              </select>
            </div>

            {/* Clear Filters */}
            {(searchQuery || selectedGenre || selectedArtist) && (
              <button
                onClick={clearFilters}
                className="btn-secondary whitespace-nowrap"
                data-testid="clear-filters-btn"
              >
                Effacer
              </button>
            )}
          </div>

          {/* Results count */}
          <p className="mt-4 text-[#A8A29E] text-sm">
            {filteredTracks.length} track{filteredTracks.length !== 1 ? 's' : ''} trouvée{filteredTracks.length !== 1 ? 's' : ''}
          </p>
        </motion.div>

        {/* Tracks Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="card animate-pulse">
                <div className="flex gap-4 p-4">
                  <div className="w-20 h-20 bg-[#1a1a1a] rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-[#1a1a1a] rounded w-3/4" />
                    <div className="h-3 bg-[#1a1a1a] rounded w-1/2" />
                    <div className="h-3 bg-[#1a1a1a] rounded w-1/3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredTracks.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <Music className="w-16 h-16 text-[#A8A29E] mx-auto mb-4" />
            <h3 className="font-display text-xl mb-2">Aucune track trouvée</h3>
            <p className="text-[#A8A29E]">Essayez de modifier vos filtres</p>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTracks.map((track, index) => {
              const isCurrentTrack = currentTrack?.id === track.id;
              
              return (
                <motion.div
                  key={track.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`card group cursor-pointer ${isCurrentTrack ? 'border-[#FFB347]' : ''}`}
                  onClick={() => playTrack(track, filteredTracks)}
                  data-testid={`track-card-${index}`}
                >
                  <div className="flex gap-4 p-4">
                    {/* Cover */}
                    <div className="relative w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={track.cover_image || "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=200"}
                        alt={track.title}
                        className="w-full h-full object-cover"
                      />
                      <div className={`absolute inset-0 bg-[#0A0908]/60 flex items-center justify-center transition-opacity ${isCurrentTrack && isPlaying ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                        <div className={`w-10 h-10 rounded-full bg-[#FFB347] flex items-center justify-center ${isCurrentTrack && isPlaying ? 'animate-pulse-glow' : ''}`}>
                          <Play className="w-5 h-5 text-[#0A0908] ml-0.5" />
                        </div>
                      </div>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-display font-semibold truncate transition-colors ${isCurrentTrack ? 'text-[#FFB347]' : 'group-hover:text-[#FFB347]'}`}>
                        {track.title}
                      </h3>
                      <p className="text-[#A8A29E] text-sm truncate">{track.artist}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-[#8B4513]">
                        <span className="px-2 py-0.5 rounded-full bg-[#8B4513]/20">
                          {track.genre}
                        </span>
                        {track.duration && (
                          <span className="flex items-center gap-1 text-[#A8A29E]">
                            <Clock className="w-3 h-3" />
                            {track.duration}
                          </span>
                        )}
                      </div>
                      {track.plays > 0 && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-[#A8A29E]">
                          <TrendingUp className="w-3 h-3" />
                          {track.plays.toLocaleString()} {t('catalog.plays')}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {filteredTracks.length === 0 && !loading && (
          <div className="text-center py-12">
            <p className="text-[#A8A29E]">{t('catalog.noTracks')}</p>
            <p className="text-sm text-[#8B4513] mt-2">{t('catalog.tryFilters')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CatalogPage;
